import { createReducer } from '@reduxjs/toolkit';
import {
  fetchVerticalNavFlag,
  receiveRemoteVerticalNav,
  RemoteVerticalNavReport
} from '../actions/vertical-nav.action';

export interface VerticalNavState {
  /** null until a read resolves for the context named below. */
  isEnabled: boolean | null;
  /** The context this answer belongs to; null before any answer. */
  tenantId: string | null;
  serviceLine: string | null;
  /** The context of the most recent read; a result for any other context arrived too late. */
  requestedKey: string | null;
  /** Only meaningful while the worklist widget is mounted. */
  remote: RemoteVerticalNavReport | null;
  /** Highest widget generation heard from; anything below it is a superseded mount. */
  remoteGeneration: number | null;
}

const initialState: VerticalNavState = {
  isEnabled: null,
  tenantId: null,
  serviceLine: null,
  requestedKey: null,
  remote: null,
  remoteGeneration: null
};

const contextKey = (
  tenantId: string | undefined,
  serviceLine: string | undefined
): string => `${tenantId ?? ''}::${serviceLine ?? ''}`;

const reducer = createReducer(initialState, builder => {
  builder.addCase(fetchVerticalNavFlag.pending, (state, action) => {
    state.requestedKey = contextKey(
      action.meta.arg.tenantId,
      action.meta.arg.serviceLine
    );
  });
  builder.addCase(fetchVerticalNavFlag.fulfilled, (state, action) => {
    // Reads are not cancelled on a context switch, and the retry can outlive several of them.
    // A result for a context nobody is waiting on any more must not replace the current answer.
    const answered = contextKey(
      action.payload.tenantId,
      action.payload.serviceLine
    );
    if (state.requestedKey !== null && state.requestedKey !== answered) return;

    state.isEnabled = action.payload.isEnabled;
    state.tenantId = action.payload.tenantId;
    state.serviceLine = action.payload.serviceLine;
  });
  // No `.rejected` case: the thunk resolves with isEnabled null when every attempt failed, so
  // a rejection is exceptional and must leave the previous answer alone rather than guess.
  builder.addCase(receiveRemoteVerticalNav, (state, action) => {
    const { generation } = action.payload;
    if (
      generation !== undefined &&
      state.remoteGeneration !== null &&
      generation < state.remoteGeneration
    ) {
      return;
    }

    state.remote = action.payload;
    if (generation !== undefined) state.remoteGeneration = generation;
  });
});

/** An answer read for a different tenant or service line counts as unresolved, not as "off". */
export const selectVerticalNavEnabled = (
  state: any,
  tenantId: string | undefined,
  serviceLine: string | undefined
): boolean | null => {
  const slice: VerticalNavState | undefined = state?.verticalNav;
  if (!slice || !tenantId || !serviceLine) return null;
  if (slice.tenantId !== tenantId || slice.serviceLine !== serviceLine) {
    return null;
  }
  return slice.isEnabled;
};

/** `undefined` when the remote has not reported for this context. */
export const selectRemoteVerticalNav = (
  state: any,
  tenantId: string | undefined,
  serviceLine: string | undefined
): boolean | null | undefined => {
  const remote: RemoteVerticalNavReport | null | undefined =
    state?.verticalNav?.remote;
  if (!remote || !tenantId || !serviceLine) return undefined;
  if (remote.tenantId !== tenantId || remote.serviceLine !== serviceLine) {
    return undefined;
  }
  return remote.isEnabled;
};

/**
 * The host and the worklist remote read `vertical-view-selector` over separate requests, so
 * hiding on the host's answer alone strands a manager whenever the remote's read fails: no nav
 * entry, and a worklist header with no replacement button. The remote therefore holds a veto —
 * hide only when it confirmed `true`, or when it has said nothing at all for this context (it
 * is not mounted, so there is no button either way). Every failure lands on "show".
 */
export const selectHidePrioritizeNav = (
  state: any,
  tenantId: string | undefined,
  serviceLine: string | undefined
): boolean => {
  if (selectVerticalNavEnabled(state, tenantId, serviceLine) !== true) return false;
  const remote = selectRemoteVerticalNav(state, tenantId, serviceLine);
  return remote === undefined || remote === true;
};

export default reducer;
